'use client';

import React, { createContext, useContext, useMemo, useState, PropsWithChildren } from 'react';
import type { HighlightConfig } from '@puzzlio/core';

/**
 * Ephemeral highlight-mode UI state, kept out of the play store. A game arms a highlighter
 * color (e.g. to pencil-in guesses); the actual cell edits still flow through the play store
 * (setCells), but "which highlighter is currently selected" is view state that lives here.
 *
 * `configs` is the game's static highlight palette (keyed on gameType), passed in once — it is
 * not per-board play data. The grid stays prop-driven: read `mode` here and hand it down as a
 * prop, so game packages never depend on this context.
 */
export interface HighlightContextValue {
  /** Static per-game highlight palette (mode key → color/label). Empty when the game has no highlighter. */
  configs: Record<string, HighlightConfig>;
  /** The currently-armed highlight mode key, or '' when highlighting is off. */
  mode: string;
  setMode: (mode: string) => void;
}

const HighlightContext = createContext<HighlightContextValue | null>(null);

export function HighlightProvider({
  configs,
  children,
}: PropsWithChildren<{ configs?: Record<string, HighlightConfig> }>) {
  const [mode, setMode] = useState('');
  const value = useMemo(() => ({ configs: configs ?? {}, mode, setMode }), [configs, mode]);
  return <HighlightContext.Provider value={value}>{children}</HighlightContext.Provider>;
}

export function useHighlight(): HighlightContextValue {
  const context = useContext(HighlightContext);
  if (!context) {
    throw new Error('useHighlight must be used within a HighlightProvider');
  }
  return context;
}
